import {
  Archive,
  CircleCheck,
  CircleSlash,
  CircleX,
  Clock,
  LoaderCircle,
  type LucideIcon,
  TriangleAlert,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router";

import { useCurrentOrg, useHasOrgRole } from "@/providers/auth/hooks";
import {
  ExplainedBadge,
  ExplainedBadgeFooter,
} from "@/shared/components/ExplainedBadge";
import { IngestionStateIcon } from "@/shared/components/IngestionStateIcon";
import {
  FormattedDateTime,
  formatRelativeTime,
  isRecent,
} from "@/shared/datetime";
import {
  IngestionLabel,
  IngestionStatus,
  ingestionLabel,
  type FileRecord,
} from "@/shared/domain/files";
import {
  fileExtension,
  filePatternLabel,
  rulePrefillForFile,
  type DefaultIngestionRule,
  INGEST_FILE_OUTCOME_MESSAGES,
  type IngestionRulePrefill,
  type IngestionRuleRecord,
  type IngestionRun,
} from "@/shared/domain/ingestionRules";
import {
  useDefaultIngestionRules,
  useIngestFiles,
  useIngestionRulesMatching,
} from "@/shared/domain/ingestionRules/hooks";
import {
  ButtonLink,
  RobotoButton,
  Spinner,
  type BadgeVariant,
} from "@/shared/foundation/primitives";
import {
  RobotoUnauthorizedException,
  userFacingErrorMessage,
} from "@/shared/http";
import { Goto } from "@/shared/navigation";

import styles from "./IngestionLabelBadge.module.css";
import {
  FIRST_RUN_GRACE_MS,
  mayHaveRun,
  useIsOrgMember,
  useProvidedIngestionRun,
} from "./latestIngestionRuns";
import { IngestionRunLookup } from "./LatestIngestionRunsProvider";

/** Red only for a run that failed; a file part way through is not an error. */
const VARIANTS: Record<IngestionLabel, BadgeVariant> = {
  [IngestionLabel.Ingested]: "success",
  [IngestionLabel.Ingesting]: "info",
  [IngestionLabel.PartlyIngested]: "warning",
  [IngestionLabel.IngestionFailed]: "error",
  [IngestionLabel.AwaitingIngestion]: "secondary",
  [IngestionLabel.NotWaitedFor]: "secondary",
  [IngestionLabel.StoredAsIs]: "secondary",
};

const ICONS: Record<IngestionLabel, LucideIcon> = {
  [IngestionLabel.Ingested]: CircleCheck,
  [IngestionLabel.Ingesting]: LoaderCircle,
  [IngestionLabel.PartlyIngested]: TriangleAlert,
  [IngestionLabel.IngestionFailed]: CircleX,
  [IngestionLabel.AwaitingIngestion]: Clock,
  [IngestionLabel.NotWaitedFor]: CircleSlash,
  [IngestionLabel.StoredAsIs]: Archive,
};

type LabelledFile = Pick<
  FileRecord,
  "ingestion_status" | "ingestable" | "relative_path" | "org_id"
> &
  Partial<Pick<FileRecord, "file_id" | "created">>;

export interface WaitSkipped {
  skippedAt: string | null;
  skippedBy: string | null;
}

export interface IngestionLabelBadgeProps {
  /**
   * Without `file_id` the badge cannot look up the file's runs or ingest it;
   * without `created`, a file whose rule has not started on it shows as
   * stalled, even one uploaded moments ago.
   */
  file: LabelledFile;
  /** Places the label within its container, e.g. pushed to the end of a table cell. */
  className?: string;
  /**
   * The file's latest ingestion run, or null when it has none. Undefined
   * means unknown: the badge reads it from a `LatestIngestionRunsProvider`,
   * or looks it up itself when its popup opens.
   */
  latestRun?: IngestionRun | null;
  /** The session the file is in stopped waiting for it. */
  waitSkipped?: WaitSkipped;
}

/**
 * A file's ingestion state as one coloured label. Clicking it opens a popup
 * that explains the state and offers what can be done about it: ingest the
 * file now, run a failed ingestion again, or add a rule for files like it.
 * Hovering does not open it, so a table of these stays quiet.
 */
export function IngestionLabelBadge({
  file,
  className,
  latestRun,
  waitSkipped,
}: IngestionLabelBadgeProps) {
  const [opened, setOpened] = React.useState(false);
  const provided = useProvidedIngestionRun(
    latestRun === undefined ? file.file_id : undefined,
  );
  const [lookedUp, setLookedUp] = React.useState<IngestionRun | null>();
  const known = latestRun !== undefined ? latestRun : provided?.run;
  const run = known !== undefined ? known : lookedUp;
  const runContext = {
    latestRun: run,
    recordPredatesRunEnd: provided?.recordPredatesRunEnd,
  };
  const label = ingestionLabel(file, {
    ...runContext,
    waitSkipped: waitSkipped !== undefined,
  });
  return (
    <>
      <ExplainedBadge
        label={label}
        variant={VARIANTS[label]}
        icon={<IngestionStateIcon icon={ICONS[label]} />}
        aria-label={`${label}: why?`}
        className={className}
        openOnHover={false}
        onOpenChange={(open) => {
          if (open) {
            setOpened(true);
          }
        }}
        explanation={
          <Explanation
            file={file}
            label={label}
            stateLabel={ingestionLabel(file, runContext)}
            run={run ?? null}
            runKnown={run !== undefined}
            waitSkipped={waitSkipped}
          />
        }
        data-testid="ingestion-label"
      />
      {opened && known === undefined && mayHaveRun(file) && (
        <IngestionRunLookup file={file} onRun={setLookedUp} />
      )}
    </>
  );
}

type IngestMutation = ReturnType<typeof useIngestFiles>;

/**
 * The popup's body: a sentence saying what the label means, a muted line on
 * what follows from it, and a footer of what can be done about it. A file the
 * session stopped waiting for also says where its own ingestion stands, so a
 * failed one can still be run again.
 */
function Explanation({
  file,
  label,
  stateLabel,
  run,
  runKnown,
  waitSkipped,
}: {
  file: LabelledFile;
  label: IngestionLabel;
  /** The label the file would have if no session had stopped waiting for it. */
  stateLabel: IngestionLabel;
  run: IngestionRun | null;
  /** Whether ``run`` is known, so a null one means no run ever started. */
  runKnown: boolean;
  waitSkipped: WaitSkipped | undefined;
}) {
  const ingest = useIngestFiles(file.org_id);
  const state = (
    <StateExplanation
      file={file}
      label={stateLabel}
      run={run}
      runKnown={runKnown}
      ingest={ingest}
    />
  );
  if (label !== IngestionLabel.NotWaitedFor) {
    return state;
  }
  return (
    <>
      {waitSkipped !== undefined && <Skipped waitSkipped={waitSkipped} />}
      {state}
    </>
  );
}

function StateExplanation({
  file,
  label,
  run,
  runKnown,
  ingest,
}: {
  file: LabelledFile;
  label: IngestionLabel;
  run: IngestionRun | null;
  runKnown: boolean;
  ingest: IngestMutation;
}) {
  switch (label) {
    case IngestionLabel.Ingested:
      return (
        <>
          <p>This file is ingested.</p>
          <p>Its topics can be visualized and queried.</p>
        </>
      );
    case IngestionLabel.Ingesting:
      return (
        <>
          <p>Roboto is ingesting this file.</p>
          <p>Its topics can be visualized and queried once it’s done.</p>
          <ExplainedBadgeFooter>
            <RunLink run={run} />
          </ExplainedBadgeFooter>
        </>
      );
    case IngestionLabel.PartlyIngested:
      return (
        <>
          <p>Roboto ingested only some of this file’s topics.</p>
          <p>Only those can be visualized and queried.</p>
          <ExplainedBadgeFooter>
            <RunLink run={run} />
          </ExplainedBadgeFooter>
        </>
      );
    case IngestionLabel.IngestionFailed:
      return <Failed file={file} run={run} ingest={ingest} />;
    case IngestionLabel.AwaitingIngestion:
      return (
        <Awaiting
          file={file}
          neverRan={runKnown && run === null}
          ingest={ingest}
        />
      );
    case IngestionLabel.StoredAsIs:
      return <StoredAsIs file={file} ingest={ingest} />;
    case IngestionLabel.NotWaitedFor:
      return null;
  }
}

function Skipped({ waitSkipped }: { waitSkipped: WaitSkipped }) {
  return (
    <>
      <p data-testid="ingestion-label-skipped">
        The session stopped waiting for this file
        {waitSkipped.skippedBy !== null && ` (${waitSkipped.skippedBy})`}
        {waitSkipped.skippedAt !== null && (
          <>
            {" "}
            <When value={waitSkipped.skippedAt} />
          </>
        )}
        .
      </p>
      <p>It stays in the session.</p>
    </>
  );
}

function Failed({
  file,
  run,
  ingest,
}: {
  file: LabelledFile;
  run: IngestionRun | null;
  ingest: IngestMutation;
}) {
  return (
    <>
      <p>
        {run?.status === "finished"
          ? "The ingestion action finished but didn’t ingest this file."
          : "Roboto couldn’t ingest this file."}
      </p>
      <p>
        {file.ingestion_status === IngestionStatus.PartlyIngested
          ? "Only some of its topics can be visualized and queried."
          : "Its topics can’t be visualized or queried until it’s ingested."}
      </p>
      {run !== null && run.detail !== null && run.detail !== "" && (
        <pre className={styles.detail} data-testid="ingestion-label-detail">
          {run.detail}
        </pre>
      )}
      <ExplainedBadgeFooter>
        <RunLink run={run} />
        <IngestButton file={file} ingest={ingest}>
          Run again
        </IngestButton>
      </ExplainedBadgeFooter>
    </>
  );
}

function Awaiting({
  file,
  neverRan,
  ingest,
}: {
  file: LabelledFile;
  /** No rule's trigger has ever run on the file. */
  neverRan: boolean;
  ingest: IngestMutation;
}) {
  const matching = useMatchingRules(file);
  const autoRule = matching.data?.find(autoIngests);
  const pastGrace =
    file.created === undefined ||
    Date.now() - Date.parse(file.created) > FIRST_RUN_GRACE_MS;
  const stalled = autoRule !== undefined && neverRan && pastGrace;
  return (
    <>
      <AwaitingSummary
        matching={matching}
        autoRule={autoRule}
        stalled={stalled}
      />
      <p>Sessions holding it wait until it’s ingested.</p>
      <MatchingError matching={matching} />
      {stalled && (
        <ExplainedBadgeFooter>
          <IngestButton file={file} ingest={ingest}>
            Ingest now
          </IngestButton>
        </ExplainedBadgeFooter>
      )}
    </>
  );
}

function AwaitingSummary({
  matching,
  autoRule,
  stalled,
}: {
  matching: MatchingRules;
  autoRule: IngestionRuleRecord | undefined;
  stalled: boolean;
}) {
  if (!matching.isSuccess) {
    return matching.isLoading ? (
      <Spinner />
    ) : (
      <p>This file is marked for ingestion but isn’t ingested yet.</p>
    );
  }
  if (autoRule !== undefined) {
    return (
      <p
        data-testid={
          stalled ? "ingestion-label-never-ran" : "ingestion-label-will-ingest"
        }
      >
        The rule <RuleLink rule={autoRule} />{" "}
        {stalled
          ? "hasn’t started ingesting this file."
          : "will ingest this file."}
      </p>
    );
  }
  if (matching.data.length > 0) {
    return (
      <p data-testid="ingestion-label-nothing-will-ingest">
        <NotAutoIngested rules={matching.data} files="this file" />
      </p>
    );
  }
  return (
    <p data-testid="ingestion-label-no-rule">
      No rule matches this file any more.
    </p>
  );
}

function StoredAsIs({
  file,
  ingest,
}: {
  file: LabelledFile;
  ingest: IngestMutation;
}) {
  const matching = useMatchingRules(file);
  const isAdmin = useHasOrgRole("admin", { orgId: file.org_id });
  if (!matching.isSuccess) {
    return matching.isLoading ? (
      <Spinner />
    ) : (
      <>
        <p>This file is available as uploaded.</p>
        <p>Its topics can’t be visualized or queried until it’s ingested.</p>
        <MatchingError matching={matching} />
      </>
    );
  }
  const rules = matching.data;
  const autoRule = rules.find(autoIngests);
  if (autoRule !== undefined) {
    return (
      <>
        <p data-testid="ingestion-label-rule-matches">
          The rule <RuleLink rule={autoRule} /> was added after this file was
          uploaded.
        </p>
        <p>Ingest it now to visualize and query its topics.</p>
        <ExplainedBadgeFooter>
          <IngestButton file={file} ingest={ingest}>
            Ingest now
          </IngestButton>
        </ExplainedBadgeFooter>
      </>
    );
  }
  if (rules.length === 0) {
    return <NoRuleMatches file={file} />;
  }
  const autoIngestOff = rules.filter(hasAction);
  return (
    <>
      <p data-testid="ingestion-label-nothing-will-ingest">
        <NotAutoIngested rules={rules} files="files like this" />
      </p>
      {autoIngestOff.length > 0 && (
        <p>
          {isAdmin ? "Turn " : "An org admin can turn "}
          {autoIngestOff.length === 1 ? "it" : "them"} on to visualize and query
          files like this.
        </p>
      )}
      {autoIngestOff.length === 1 && (
        <ExplainedBadgeFooter>
          <ButtonLink
            href={Goto.createIngestionRuleRoute(autoIngestOff[0].rule_id)}
            variant="primary"
            size="sm"
            data-testid="ingestion-label-open-rule"
          >
            Open rule
          </ButtonLink>
        </ExplainedBadgeFooter>
      )}
    </>
  );
}

/**
 * Why none of the rules that match a file ingest it. A rule with an action
 * has its auto-ingest off, which turning it on fixes; one without an action
 * expects something else, such as a trigger or the SDK, to do the ingesting.
 * When both kinds match, only the ones that could be turned on are named.
 */
function NotAutoIngested({
  rules,
  files,
}: {
  rules: IngestionRuleRecord[];
  /** What the rules ingest: "this file", or "files like this". */
  files: string;
}) {
  const autoIngestOff = rules.filter(hasAction);
  if (autoIngestOff.length > 0) {
    const one = autoIngestOff.length === 1;
    return (
      <>
        <TheRules rules={autoIngestOff} /> {one ? "matches" : "match"} this
        file, but {one ? "its" : "their"} auto-ingest is off.
      </>
    );
  }
  const one = rules.length === 1;
  return (
    <>
      <TheRules rules={rules} /> {one ? "expects" : "expect"} something besides{" "}
      {one ? "it" : "them"} to ingest {files}.
    </>
  );
}

/**
 * The rules that match the file. Its callers are in the popup, whose content
 * mounts only while open, so this fetches only then, and never for a viewer
 * outside the file's org.
 */
function useMatchingRules(file: LabelledFile) {
  const isMember = useIsOrgMember(file.org_id);
  return useIngestionRulesMatching(file.relative_path, {
    enabled: isMember,
    orgId: file.org_id,
  });
}

type MatchingRules = ReturnType<typeof useMatchingRules>;

function hasAction(rule: IngestionRuleRecord): boolean {
  return rule.invocation !== null;
}

function autoIngests(rule: IngestionRuleRecord): boolean {
  return rule.auto_ingest && hasAction(rule);
}

function MatchingError({ matching }: { matching: MatchingRules }) {
  return matching.isError ? (
    <p className={styles.error} role="alert">
      {userFacingErrorMessage(matching.error)}
    </p>
  ) : null;
}

function RuleLink({ rule }: { rule: IngestionRuleRecord }) {
  return (
    <Link
      to={Goto.createIngestionRuleRoute(rule.rule_id)}
      className={styles.ruleLink}
    >
      {rule.name}
    </Link>
  );
}

/** "The rule A", or "The rules A and B" / "A, B and C". */
function TheRules({ rules }: { rules: IngestionRuleRecord[] }) {
  return (
    <>
      {rules.length === 1 ? "The rule " : "The rules "}
      {rules.map((rule, index) => (
        <React.Fragment key={rule.rule_id}>
          {index > 0 && (index === rules.length - 1 ? " and " : ", ")}
          <RuleLink rule={rule} />
        </React.Fragment>
      ))}
    </>
  );
}

function RunLink({ run }: { run: IngestionRun | null }) {
  return run !== null && run.invocation_id !== null ? (
    <ButtonLink
      href={Goto.createInvocationRoute(run.invocation_id)}
      variant="secondary"
      size="sm"
      data-testid="ingestion-label-run-link"
    >
      View invocation
    </ButtonLink>
  ) : null;
}

/** "3 minutes ago", or "on <date>" past a day. */
function When({ value }: { value: string }) {
  return isRecent(value) ? (
    <>{formatRelativeTime(value)}</>
  ) : (
    <>
      on <FormattedDateTime value={value} />
    </>
  );
}

/**
 * Ingests the file with the rule that matches it. Renders nothing without a
 * `file_id` or for a viewer outside the file's org; the server checks that
 * the viewer may edit the file.
 */
function IngestButton({
  file,
  ingest,
  children,
}: React.PropsWithChildren<{ file: LabelledFile; ingest: IngestMutation }>) {
  const isMember = useIsOrgMember(file.org_id);
  const fileId = file.file_id;
  if (fileId === undefined || !isMember) {
    return null;
  }
  const result = ingest.data?.find((entry) => entry.file_id === fileId);
  return (
    <>
      <RobotoButton
        variant="primary"
        size="sm"
        loading={ingest.isPending}
        onClick={() => ingest.mutate([fileId])}
        data-testid="ingestion-label-ingest"
      >
        {children}
      </RobotoButton>
      {result !== undefined && (
        <p role="status" data-testid="ingestion-label-ingest-outcome">
          {INGEST_FILE_OUTCOME_MESSAGES[result.outcome]}
          {result.outcome === "started" && result.invocation_id !== null && (
            <>
              {" "}
              <Link to={Goto.createInvocationRoute(result.invocation_id)}>
                View invocation
              </Link>
            </>
          )}
        </p>
      )}
      {ingest.isError && (
        <p
          className={styles.error}
          role="alert"
          data-testid="ingestion-label-ingest-outcome"
        >
          {ingest.error instanceof RobotoUnauthorizedException
            ? "You need permission to edit this file to ingest it."
            : userFacingErrorMessage(ingest.error)}
        </p>
      )}
    </>
  );
}

/**
 * For a file no rule matches. Only org admins can write rules, and only in
 * the org they are working in, so only they get the button that adds one.
 */
function NoRuleMatches({ file }: { file: LabelledFile }) {
  const isAdmin = useHasOrgRole("admin", { orgId: file.org_id });
  const inFileOrg = useCurrentOrg()?.org_id === file.org_id;
  const canAddRule = inFileOrg && isAdmin;
  return (
    <>
      <p data-testid="ingestion-label-no-rule">
        No rule matches this file, so it’s available as uploaded.
      </p>
      <p>
        {canAddRule
          ? "If it contains topic data, add an ingestion rule to visualize and query it."
          : "If it contains topic data, an org admin can add an ingestion rule for it."}
      </p>
      {canAddRule && (
        <ExplainedBadgeFooter>
          <AddRule file={file} />
        </ExplainedBadgeFooter>
      )}
    </>
  );
}

/**
 * A rule for every file of this one's kind. When Roboto has a default rule
 * for the kind (`**\/*.ulg` → `ulog_ingestion`), the new rule starts from it,
 * action included.
 */
function rulePrefillWithDefaults(
  relativePath: string,
  defaults: readonly DefaultIngestionRule[],
): IngestionRulePrefill {
  const prefill = rulePrefillForFile(relativePath);
  const [pattern] = prefill.includePatterns;
  const match = defaults.find((rule) =>
    rule.include_patterns.some(
      (include) => include.toLowerCase() === pattern?.toLowerCase(),
    ),
  );
  if (match === undefined) {
    return prefill;
  }
  return {
    name: match.name,
    includePatterns: [...match.include_patterns],
    excludePatterns: [...match.exclude_patterns],
    invocation: match.invocation,
  };
}

/** "Add a rule for `.ulg` files", or for a file with no extension, its name. */
function AddRule({ file }: { file: LabelledFile }) {
  const navigate = useNavigate();
  const defaults = useDefaultIngestionRules(file.org_id);
  const extension = fileExtension(file.relative_path);
  return (
    <RobotoButton
      variant="primary"
      size="sm"
      loading={defaults.isLoading}
      onClick={() =>
        void navigate(
          Goto.createNewIngestionRuleRoute(
            rulePrefillWithDefaults(file.relative_path, defaults.data ?? []),
          ),
        )
      }
      data-testid="ingestion-label-add-rule"
    >
      Add a rule for{" "}
      <code className={styles.pattern}>
        {filePatternLabel(file.relative_path)}
      </code>
      {extension !== null && " files"}
    </RobotoButton>
  );
}
